/* Client calls to the secured account endpoints on the worker. */
import type { RoleKey } from "./permissions";

export interface Account { email: string; name: string; role: RoleKey; }
export interface Member { email: string; name: string | null; role: RoleKey; created_at?: string; }

async function post(url: string, body?: any) {
  const r = await fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "same-origin", body: body ? JSON.stringify(body) : undefined });
  const data = await r.json().catch(() => ({}));
  return { ok: r.ok, status: r.status, data } as { ok: boolean; status: number; data: any };
}

export async function apiMe(): Promise<Account | null> {
  try {
    const r = await fetch("/api/auth/me", { credentials: "same-origin" });
    const d = (await r.json()) as { user?: Account | null };
    return d.user ?? null;
  } catch { return null; }
}
export async function apiSignup(email: string, password: string, name: string, invite?: string): Promise<Account> {
  const { ok, data } = await post("/api/auth/signup", { email, password, name, invite });
  if (!ok) throw new Error(data.error || "Could not create account.");
  return data as Account;
}
export async function apiLogin(email: string, password: string): Promise<Account> {
  const { ok, data } = await post("/api/auth/login", { email, password });
  if (!ok) throw new Error(data.error || "Could not sign in.");
  return data as Account;
}
export async function apiLogout(): Promise<void> { await post("/api/auth/logout"); }

export async function apiMembers(): Promise<Member[]> {
  try {
    const r = await fetch("/api/auth/members", { credentials: "same-origin" });
    const d = (await r.json()) as { members?: Member[] };
    return d.members ?? [];
  } catch { return []; }
}
export async function apiSetRole(email: string, role: RoleKey): Promise<void> {
  const { ok, data } = await post("/api/auth/role", { email, role });
  if (!ok) throw new Error(data.error || "Could not update role.");
}

/** Resolve a client access code against the live workspace. Public by design —
    the client has no account, and their device holds no workspace to search.
    The rate card comes with it, or the portal would price the project off the
    app's defaults and quote a different fee than the studio sees. */
export async function apiPortalProject(code: string): Promise<{ project: any; rateCard: any } | null> {
  try {
    const r = await fetch(`/api/portal/${encodeURIComponent(code)}`);
    if (!r.ok) return null;
    const d = (await r.json()) as { project?: any; rateCard?: any };
    return d.project ? { project: d.project, rateCard: d.rateCard ?? null } : null;
  } catch { return null; }
}

/* ---- invitations ---- */
export interface Invite { token: string; email: string; name: string; role: RoleKey; created_at?: string; }

export async function apiCreateInvite(email: string, name: string, role: RoleKey): Promise<Invite> {
  const { ok, data } = await post("/api/auth/invite", { email, name, role });
  if (!ok) throw new Error(data.error || "Could not create the invitation.");
  return data as Invite;
}
/** Public — the invitee has no account yet. */
export async function apiReadInvite(token: string): Promise<{ valid: boolean; email: string; name: string; role: RoleKey } | null> {
  try {
    const r = await fetch(`/api/auth/invite/${encodeURIComponent(token)}`);
    const d = await r.json() as any;
    return d?.valid ? d : null;
  } catch { return null; }
}
export async function apiInvites(): Promise<Invite[]> {
  try {
    const r = await fetch("/api/auth/invites", { credentials: "same-origin" });
    const d = (await r.json()) as { invites?: Invite[] };
    return d.invites ?? [];
  } catch { return []; }
}
export async function apiRevokeInvite(token: string): Promise<void> {
  await fetch(`/api/auth/invite/${encodeURIComponent(token)}`, { method: "DELETE", credentials: "same-origin" });
}

/* ---- integrations (admin) — the Mind Map key, set from the app ---- */
export interface Integrations { mindmap: boolean; source: "app" | "secret" | null; base: string; }
export async function apiIntegrations(): Promise<Integrations | null> {
  try {
    const r = await fetch("/api/settings/integrations", { credentials: "same-origin" });
    if (!r.ok) return null;
    return await r.json() as Integrations;
  } catch { return null; }
}
export async function apiSetMindmapKey(key: string): Promise<void> {
  const { ok, data } = await post("/api/settings/mindmap-key", { key });
  if (!ok) throw new Error(data.error || "Could not save the key.");
}
export async function apiClearMindmapKey(): Promise<void> {
  await fetch("/api/settings/mindmap-key", { method: "DELETE", credentials: "same-origin" });
}
export async function apiPingMindmap(): Promise<{ ok: boolean; status?: number; reason?: string; error?: string }> {
  const { data } = await post("/api/mindmap/ping");
  return data;
}
